// "Jobs at once" on an org AI key card (#6863, epic #6861): the set value and
// its editor, the live usage line, and the pushback warning. Two reads feed it:
// the key's DECLARED capacity rides the keys list (`KeyRow.capacity`), and the
// live numbers — running, waiting, effective, default, learned — ride `GET
// /v1/billing/capacity` (#6862), polled every 20 s like `useFleetUtilization`.
//
// THE FOLD IS PURE AND LIVES HERE, not in the component: #6864 reuses the usage
// formatter and its copy. Its one rule is that an unknown number is never shown
// as 0 — a failed read reads "unavailable", a key the capacity view does not list
// (not an admission key: invalid, unverified, inactive) reads "not taking jobs",
// and a read still in flight renders nothing at all.
//
// The write is `lib/key-priority.ts`'s optimistic keyed mutation, copied: the
// card flips at once, rolls back THAT row on a refusal (the refusal's `detail`
// is the toast), then re-reads both the keys list and the capacity view.

import { KEY_CAPACITY_MAX, KEY_CAPACITY_MIN, KeyCapacitySource } from '@developerz/domain';
import { createQuery, type QueryClient, useQueryClient } from '@tanstack/solid-query';
import { t } from '../i18n';
import { createKeyedMutation } from './keyed-mutation';
import {
  getKeyCapacity,
  type KeyCapacityRead,
  type KeyCapacityReadRow,
  type KeyRow,
  keyCapacityQueryKey,
  keysQueryKey,
  setKeyCapacity,
} from './keys';
import { SESSION_TOKEN } from './session';
import type { ToastTone } from './toast';

export type { KeyCapacityRead, KeyCapacityReadRow } from './keys';

/** The route's bounds, from the domain seam that validates them server-side. */
export { KEY_CAPACITY_MAX, KEY_CAPACITY_MIN };

/**
 * The typed value as a capacity, or null when the route would refuse it. A UX
 * hint that spares a round trip; the route's 400 is the arbiter.
 */
export function parseKeyCapacity(raw: string): number | null {
  const s = raw.trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return n >= KEY_CAPACITY_MIN && n <= KEY_CAPACITY_MAX ? n : null;
}

/**
 * One card's reading of the capacity view. `pending` = the first read has not
 * landed (render no usage line); `unavailable` = the read failed (a 503 or any
 * error); `no_row` = the read landed and this key is not in it.
 */
export type KeyCapacityState =
  | { readonly kind: 'pending' }
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'no_row' }
  | { readonly kind: 'ok'; readonly row: KeyCapacityReadRow };

/** The query's status, as `createQuery` reports it — the fold's only input. */
export type KeyCapacityReadStatus = {
  readonly status: 'pending' | 'error' | 'success';
  readonly data: KeyCapacityRead | undefined;
};

/**
 * Fold the capacity read for one key. The ERROR is checked first: a polled
 * refetch that fails keeps the last good `data` beside it, and painting that as
 * live would be the stale-number lie this card exists not to tell.
 */
export function keyCapacityState(read: KeyCapacityReadStatus, keyId: string): KeyCapacityState {
  if (read.status === 'error') return { kind: 'unavailable' };
  if (read.status === 'pending' || read.data === undefined) return { kind: 'pending' };
  const row = read.data.keys.find((k) => k.key_id === keyId);
  return row ? { kind: 'ok', row } : { kind: 'no_row' };
}

/** "N of M running, K waiting", or "N running, K waiting" when unbounded. #6864 reuses it. */
export function keyCapacityUsageLine(row: KeyCapacityReadRow): string {
  const running = row.inflight;
  const queued = row.queued;
  const capacity = row.capacity.effective;
  return capacity === null
    ? t('account_keys.capacity_usage_unbounded', { running, queued })
    : t('account_keys.capacity_usage', { running, capacity, queued });
}

/** The card's usage line for a state; null while the first read is in flight. */
export function keyCapacityUsage(state: KeyCapacityState): string | null {
  switch (state.kind) {
    case 'pending':
      return null;
    case 'unavailable':
      return t('account_keys.capacity_usage_unavailable');
    case 'no_row':
      return t('account_keys.capacity_usage_no_row');
    case 'ok':
      return keyCapacityUsageLine(state.row);
  }
}

/**
 * The pushback warning: the provider made the platform learn a limit BELOW the
 * value an admin set. Never on Use default (`declared` null: there is no set value
 * to fall short of), and never without a read (the numbers are unknown).
 */
export function keyCapacityWarning(state: KeyCapacityState): string | null {
  if (state.kind !== 'ok') return null;
  const { source, learned, declared } = state.row.capacity;
  if (source !== KeyCapacitySource.Learned || learned === null || declared === null) return null;
  if (learned >= declared) return null;
  return t('account_keys.capacity_pushback', { learned, declared });
}

/** The default for this key's kind: a number, null for none (metered), undefined unknown. */
function knownDefault(state: KeyCapacityState): number | null | undefined {
  return state.kind === 'ok' ? state.row.capacity.default : undefined;
}

/** "D" or "no limit", or null when the read does not say. */
function defaultValue(state: KeyCapacityState): string | null {
  const d = knownDefault(state);
  if (d === undefined) return null;
  return d === null ? t('account_keys.capacity_no_limit') : String(d);
}

/** "Default (D)" / "Default (no limit)" / "Default" when the read does not say. */
export function keyCapacityDefaultLabel(state: KeyCapacityState): string {
  const value = defaultValue(state);
  return value === null
    ? t('account_keys.capacity_default_unknown')
    : t('account_keys.capacity_default', { value });
}

/** The editor's reset: "Use default (D)" / "Use default (no limit)" / "Use default". */
export function keyCapacityUseDefaultLabel(state: KeyCapacityState): string {
  const value = defaultValue(state);
  return value === null
    ? t('account_keys.capacity_use_default_unknown')
    : t('account_keys.capacity_use_default', { value });
}

/** "Jobs at once: N" — the set value, or the default when nothing is declared. */
export function keyCapacityValueLabel(declared: number | null, state: KeyCapacityState): string {
  const value = declared === null ? keyCapacityDefaultLabel(state) : String(declared);
  return t('account_keys.capacity_value', { value });
}

/** The editor's prefill: the set value, else the default when it is a number, else empty. */
export function keyCapacityDraft(declared: number | null, state: KeyCapacityState): string {
  if (declared !== null) return String(declared);
  const d = knownDefault(state);
  return typeof d === 'number' ? String(d) : '';
}

// ── the read ────────────────────────────────────────────────────────────────

/** The capacity view, polled at 20 s and keyed by token + active org. Idle with no session. */
export function useKeyCapacityRead(token: string | null = SESSION_TOKEN) {
  return createQuery<KeyCapacityRead>(() => ({
    queryKey: keyCapacityQueryKey(token),
    enabled: token !== null,
    refetchInterval: 20_000,
    queryFn: () => getKeyCapacity(token),
  }));
}

// ── the write ───────────────────────────────────────────────────────────────

export type KeyCapacityEdit = {
  /** Awaitable for tests; resolves on failure too (the toast carries it). */
  readonly save: (row: KeyRow, capacity: number | null) => Promise<void>;
  readonly isBusy: (id: string) => boolean;
};

export function createKeyCapacityEdit(input: {
  readonly client: QueryClient;
  /** The keys list's cache key — what the optimistic patch writes. */
  readonly keysKey: () => readonly unknown[];
  /** The capacity view's cache key — re-read after every save. */
  readonly capacityKey: () => readonly unknown[];
  /** `setKeyCapacity`, token pre-bound by the container. */
  readonly set: (id: string, capacity: number | null) => Promise<KeyRow>;
  readonly notify: (message: string, tone?: ToastTone) => void;
}): KeyCapacityEdit {
  const writes = createKeyedMutation({
    fallback: t('account_keys.capacity_error'),
    onError: (message) => input.notify(message, 'error'),
  });

  const patch = (key: readonly unknown[], id: string, capacity: number | null): void => {
    input.client.setQueryData<readonly KeyRow[]>(key, (rows) =>
      rows?.map((k) => (k.id === id ? { ...k, capacity } : k)),
    );
  };

  const save = async (row: KeyRow, capacity: number | null): Promise<void> => {
    const key = input.keysKey();
    await writes.run(row.id, async () => {
      // An in-flight read landing after the patch would paint the old value.
      await input.client.cancelQueries({ queryKey: key });
      patch(key, row.id, capacity);
      try {
        await input.set(row.id, capacity);
      } catch (err) {
        patch(key, row.id, row.capacity);
        throw err;
      }
      input.notify(t('account_keys.capacity_saved_toast'));
    });
    // The patch is a preview; the two reads are the answer, refused or not.
    await input.client.refetchQueries({ queryKey: key });
    await input.client.refetchQueries({ queryKey: input.capacityKey() });
  };

  return { save, isBusy: writes.isBusy };
}

/** The ORG pool's capacity edit (`write:billing`); call in a component body. */
export function useOrgKeyCapacity(
  token: string | null,
  notify: (message: string, tone?: ToastTone) => void,
): KeyCapacityEdit {
  return createKeyCapacityEdit({
    client: useQueryClient(),
    keysKey: () => keysQueryKey(token),
    capacityKey: () => keyCapacityQueryKey(token),
    set: (id, capacity) => setKeyCapacity(id, capacity, token),
    notify,
  });
}

/** What the org keys page hands each card: its reading, its busy flag, and the edit iff allowed. */
export type KeyCapacityCards = {
  readonly state: (id: string) => KeyCapacityState;
  readonly isBusy: (id: string) => boolean;
  /** Present only while `canEdit()` holds — the editor renders iff it is passed. */
  readonly onSave: ((row: KeyRow, capacity: number | null) => void) | undefined;
};

/**
 * The org page's whole capacity wiring in one call: the polled read, folded per
 * key, and the edit. Every role reads the live usage (`read:billing`); only a
 * caller `canEdit` admits gets `onSave` — the container passes its `canRank`
 * (`roleSettled` + `canAdminWrite`, `write:billing`); the 403 arbitrates.
 */
export function useOrgKeyCapacityCards(
  token: string | null,
  notify: (message: string, tone?: ToastTone) => void,
  canEdit: () => boolean,
): KeyCapacityCards {
  const read = useKeyCapacityRead(token);
  const edit = useOrgKeyCapacity(token, notify);
  return {
    state: (id) => keyCapacityState({ status: read.status, data: read.data }, id),
    isBusy: edit.isBusy,
    get onSave() {
      return canEdit()
        ? (row: KeyRow, capacity: number | null) => void edit.save(row, capacity)
        : undefined;
    },
  };
}
